import React, { useState } from "react";
import { Table,Tooltip, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Button } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CloseIcon from "@mui/icons-material/Close";
import { useStateContext } from "../../contexts/ContextProvider";
import MiniAgeMapChart from './MiniAgeMapChart';
import HelpOutlineIcon from "@mui/icons-material/HelpOutline"; // Question mark icon



// Function to shorten addresses (e.g., 0xABC1...XYZ9)
const shortenAddress = (address) => address ? `${address.slice(0, 4)}..${address.slice(-4)}` : "";
const ageIntervals = ["1w", "5d", "48h", "24h", "12h", "6h", "4h", "2h", "1h", "30m", "15m", "5m"];



// Calculate total amount but only for rows that have a valid age map


// Function to check if row data is missing
const checkRowValidity = (row) => {
  return row.bought === 0 && row.sold === 0 && row.netTransfers === 0;
};


// Sample data with 8 entries
const defaultData = [
  { address: "0xABC1234567890abcdefABC1234567890abcdef", tokenBalance: "1500", avgWeightedEntry: "0.0023", bought: "500", sold: "200", netTransfers: "300" },
  { address: "0xDEF4567890abcdefDEF4567890abcdefDEF456", tokenBalance: "3200", avgWeightedEntry: "0.0018", bought: "1000", sold: "600", netTransfers: "400" },
  { address: "0xGHI789abcdef123456GHI789abcdef123456GHI", tokenBalance: "890", avgWeightedEntry: "0.0031", bought: "300", sold: "100", netTransfers: "200" },
  { address: "0xJKL101112abcdefJKL101112abcdefJKL101112", tokenBalance: "5600", avgWeightedEntry: "0.0015", bought: "2000", sold: "500", netTransfers: "-1500" },
  { address: "0xMNO131415abcdefMNO131415abcdefMNO131415", tokenBalance: "4500", avgWeightedEntry: "0.0020", bought: "1200", sold: "400", netTransfers: "800" },
  { address: "0xPQR161718abcdefPQR161718abcdefPQR161718", tokenBalance: "6780", avgWeightedEntry: "0.0017", bought: "900", sold: "300", netTransfers: "600" },
  { address: "0xSTU192021abcdefSTU192021abcdefSTU192021", tokenBalance: "7200", avgWeightedEntry: "0.0025", bought: "1300", sold: "600", netTransfers: "700" },
  { address: "0xVWX222324abcdefVWX222324abcdefVWX222324", tokenBalance: "5000", avgWeightedEntry: "0.0028", bought: "1100", sold: "500", netTransfers: "600" },
];


  // Format total supply & net transfers into "M" format
const formatToMillions = (value) =>
    value ? (value / 1_000_000).toFixed(1) : 0;

const formatAmount = (value) => {
  if (value >= 1_000_000) {
      return (value / 1_000_000).toFixed(1) + 'M';
  } else {
      return (value / 1_000).toFixed(0) + 'k';
  }
};

function formatToK(amount) {
  const num = amount / 1000;
  const formattedNumber = num.toFixed(1).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${formattedNumber}k`;
}

function formatBalanceWithPercentage(balance, tokenSupply) {
  console.log("balance " + balance+ "token supply "+ tokenSupply);
  const balanceFloat = parseFloat(balance);
  const formattedBalance = formatToMillions(balanceFloat);
  const percentage = ((balanceFloat / tokenSupply) * 100).toFixed(2).replace('.', ',');
  
  return `${formattedBalance}  M   - - -  ${percentage}%`;
}
const extractNumericBalance = (balanceStr) => {
  if (!balanceStr) return 0;
  
  // Extract numeric part from "X.X M - - - Y%"
  const match = balanceStr.match(/([\d,]+)\s*M/);
  
  if (match && match[1]) {
    return parseFloat(match[1].replace(',', '.')) * 1_000_000; // Convert back to integer
  }
  
  return 0;
};


const calculateTotalAmount = (data) => {
  if (!Array.isArray(data) || data.length === 0) return 0; // ✅ Prevent errors

  return data.reduce((sum, row) => {
    if (row.holder && row.holder.balance) {
      const balance = parseFloat(row.holder.balance);
      if (!isNaN(balance)) {
        return sum + balance; // ✅ Only add valid numeric balances
      }
    }
    return sum;
  }, 0);
};

const formatHolderData = (rawData, tokenSupply) => {
  
  return rawData.map(({ holder, statsRealtime, ageMap }) => ({
    address: holder.owner,
    tokenBalance: formatBalanceWithPercentage(holder.balance, tokenSupply), 
    avgWeightedEntry: formatAmount(statsRealtime.buy.avgMCap), // Keep as a number
    bought: statsRealtime.buy?.amount ? formatToMillions(statsRealtime.buy.amount) : 0,
    sold: statsRealtime.sell?.amount ? formatToMillions(statsRealtime.sell.amount) : 0,
    pnl: statsRealtime.usdPnLRealized ? statsRealtime.usdPnLRealized.toFixed(0) : 0,

    netTransfers: parseFloat(holder.balance) - parseFloat(statsRealtime.totalCalculatedSize), // Keep as a number
    chartData: {
      avgEntry: formatAmount(statsRealtime.buy.avgMCap),
      ...ageMap, // Spread all ageMap intervals directly into chartData
    }
  }));
};



const TableData = ({ input = null, tokenSupply=null }) => {


  const data = input && input.length ? formatHolderData(input,tokenSupply) : defaultData;
  const [showAll, setShowAll] = useState(false);
  const visibleData = showAll ? data : data.slice(0, 10);

  const totalAmount = calculateTotalAmount(input);



  const relativePercentage = tokenSupply ? ((totalAmount / tokenSupply) * 100).toFixed(2) : 0;

  console.log("table input",input);
  console.log("DatA > ", data);
  const [selectedRow, setSelectedRow] = useState(null);

  const handleRowClick = (index) => {
  };

  const handleDeselectRow = (e) => {
    e.stopPropagation();
    setSelectedRow(null);
  };

  const headerCellStyle = {
    color: "rgba(209, 213, 219, 0.9)",
    backgroundColor: "#0d0d0f",
    textAlign: "center",
    paddingLeft: "10px",
    paddingRight: "10px",
    minWidth: "calc(100% / 7 + 20px)", // Ensures even expansion
  };

  return (
    <>
      {/* 📌 Total and Relative Amount Section */}
      <div className="flex justify-start items-start mb-1 text-gray-400 text-sm">
        <span style={{ marginRight: "50px" }}>Total in table: {formatToMillions(totalAmount)} M</span>
        <span>Relative: {relativePercentage} %</span>
    </div>

    <div
      style={{
        borderRadius: "10px", // Rounded corners on the wrapper, NOT TableContainer
        overflow: "hidden",   // Ensures no gaps appear due to border rounding
        border: "2px solid rgba(107, 114, 128, 0.5)", // Apply border to wrapper
        padding: "2px", // Small internal padding for better visual consistency
      }}
    >
    <TableContainer 
      component={Paper} 
      sx={{
        maxHeight: "400px",
        overflow: "auto",
        backgroundColor: "transparent", // Transparent background
        boxShadow: "none",
        border:"none",
      }}
    >
      <Table stickyHeader >
        <TableHead sx={{ backgroundColor: "#0d0d0f"}}>
        <TableRow sx={{ backgroundColor: "#0d0d0f"}}>
        <TableCell sx={{ ...headerCellStyle, textAlign: "left" }}>Address</TableCell>
        <TableCell sx={headerCellStyle}>
          Age map
          <Button 
            size="small"
            onClick={() => setShowAll((prev) => !prev)}
            sx={{ 
              color: "#6b7280", 
              fontSize: "0.7rem", 
              textTransform: "none", 
              border: "1px solid #6b7280", 
              borderRadius: "6px",
              px: 1,
              py: 0.2,
              ml: 1
            }}
          >
            {showAll ? 'Hide' : 'Load All'}
          </Button>
        </TableCell>
        <TableCell sx={headerCellStyle}>Token Balance</TableCell>
        <TableCell sx={headerCellStyle}>Avg Weighted Entry</TableCell>
        <TableCell sx={headerCellStyle}>Bought (M)</TableCell>
        <TableCell sx={headerCellStyle}>Sold (M)</TableCell>
        <TableCell sx={headerCellStyle}>Net Transfers In (M)</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {visibleData.map((row, index) => (
            <TableRow
              key={index}
              onClick={() => handleRowClick(index)}
              sx={{
                cursor: "pointer",
                backgroundColor: selectedRow === index ? "rgba(128, 18, 231, 0.2)" : "transparent",
                "&:hover": {
                  backgroundColor: selectedRow === index ? "none":"rgba(255, 255, 255, 0.08)", // Light gray transparent on hover
                },
                border: selectedRow === index ? "2px solid #8018e7" : "none", // Purple border if selected
                height: "30px", // Reduce vertical empty space
              }}
            >
              <TableCell sx={{ color: "rgba(156, 163, 175, 0.9)", padding: "6px 10px" }}>
                <span>{index + 1} &nbsp;|&nbsp;  {shortenAddress(row.address)}</span>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent row selection when copying
                    navigator.clipboard.writeText(row.address);
                  }}
                  title="Copy to Clipboard"
                  sx={{
                    color: "rgba(156, 163, 175, 0.9)",
                    "&:hover": { color: "#3b82f6" },
                  }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </TableCell>
              <TableCell sx={{ color: "rgba(156, 163, 175, 0.9)", padding: "6px 10px", textAlign:"center" }}>
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%" }}>
              {checkRowValidity(row) ? (
                    <Tooltip title="Failed to fetch data">
                      <HelpOutlineIcon sx={{ color: "#eab308", cursor: "default" }} />
                    </Tooltip>
                  ) : (
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%" }}>
                      <MiniAgeMapChart data={row.chartData} />
                    </div>
                  )}
              </div>
              </TableCell>
              <TableCell sx={{ color: "rgba(156, 163, 175, 0.9)", padding: "6px 10px", textAlign:"center" }}>{row.tokenBalance}</TableCell>
              <TableCell sx={{ color: "rgba(156, 163, 175, 0.9)", padding: "6px 10px", textAlign:"center" }}>{row.avgWeightedEntry}</TableCell>
              <TableCell sx={{ color: "rgba(156, 163, 175, 0.9)", padding: "6px 10px", textAlign:"center" }}>{row.bought}</TableCell>
              <TableCell sx={{ color: "rgba(156, 163, 175, 0.9)", padding: "6px 10px", textAlign:"center" }}>{row.sold}</TableCell>
              <TableCell sx={{ color: row.netTransfers >= 0 ? "#3aba69" : "#c23434", padding: "6px 10px", textAlign:"center" }}>{formatToMillions(row.netTransfers)}</TableCell>
              
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
    </div>
    </>
  );
};

export default TableData;
